import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Button,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import React, { useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterDropdownProps {
  filterOptions: FilterOption[];
  onApply: (selectedFilters: string[]) => void;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  filterOptions = [],
  onApply,
}) => {
  const { colors } = useCelleraTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFilters((prev) =>
      event.target.checked
        ? [...prev, event.target.name]
        : prev.filter((name) => name !== event.target.name)
    );
  };

  const handleApply = (clear: boolean) => {
    if (clear) {
      onApply([]);
      setSelectedFilters([]);
    } else {
      onApply(selectedFilters);
    }
    setAnchorEl(null);
  };

  return (
    <Box className="p-0 m-0">
      <Button
        id="filter-dropdown-button"
        sx={{
          "&:hover": {
            borderColor: colors.background.darkGreenHighlight,
            "& #filter-dropdown-count": {
              backgroundColor: colors.background.darkGreenHighlight,
            },
          },
          color: colors.text.primary,
          backgroundColor: colors.background.paper,
          transition: "background-color 0.2s ease",
        }}
        variant="outlined"
        startIcon={<FilterListIcon />}
        onClick={handleClick}
      >
        Filter
        <Box
          id="filter-dropdown-count"
          className="rounded-full flex items-center justify-center ml-2"
          sx={{
            backgroundColor: colors.primary.main,
            color: colors.text.inverse,
            transition: "background-color 0.2s ease",
            width: "23px",
            height: "23px",
          }}
        >
          {selectedFilters.length}
        </Box>
      </Button>
      <Menu
        id="filter-dropdown-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "3px",
            p: 0,
            m: 0,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: 500,
            maxHeight: 800,
          }}
        >
          <Typography variant="h5" sx={{ pl: 2, pt: 1 }}>
            Filter
          </Typography>
          <Divider />
          {filterOptions.map((option) => (
            <MenuItem key={option.value}>
              <FormControlLabel
                sx={{
                  width: "100%",
                }}
                control={
                  <Checkbox
                    checked={selectedFilters.includes(option.value)}
                    onChange={handleChange}
                    name={option.value}
                  />
                }
                label={option.label}
              />
            </MenuItem>
          ))}
        </Box>
        <Divider />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3, p: 1 }}>
          <Button
            onClick={() => handleApply(true)}
            variant="outlined"
            color="primary"
            fullWidth
            sx={{
              width: "30%",
              borderRadius: "80px",
              borderColor: colors.border.main,
              color: colors.text.primary,
              "&:hover": {
                borderColor: colors.border.dark,
                backgroundColor: colors.background.secondary,
              },
            }}
          >
            Reset
          </Button>
          <Button
            onClick={() => handleApply(false)}
            className="rounded-full"
            variant="outlined"
            fullWidth
            disabled={selectedFilters.length === 0}
            sx={{
              borderRadius: "80px",
              width: "30%",
              borderColor: colors.border.main,
              color: colors.text.primary,
              "&:hover": {
                borderColor: colors.border.dark,
                backgroundColor: colors.background.secondary,
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

export default FilterDropdown;
